import { Link } from 'react-router-dom';
import {
  BarChart3,
  Braces,
  CalendarDays,
  Database,
  GitMerge,
  LayoutGrid,
  LineChart,
  Sparkles,
  Table2,
  Target,
  Terminal,
  Trophy,
} from 'lucide-react';
import { Button } from '../components/ui/Button.js';
import { Card } from '../components/ui/Card.js';
import { useAuth } from '../lib/auth.js';

const CURRICULUM = [
  {
    level: 1,
    title: 'SQL Fundamentals',
    detail: 'SELECT, WHERE, ORDER BY, NULL handling',
    icon: Terminal,
  },
  { level: 2, title: 'Aggregations', detail: 'COUNT, SUM, GROUP BY, HAVING', icon: BarChart3 },
  {
    level: 3,
    title: 'Joins',
    detail: 'INNER, LEFT, self joins, and the mistakes that bite',
    icon: GitMerge,
  },
  {
    level: 4,
    title: 'Data Cleaning',
    detail: 'CASE, COALESCE, CAST, duplicate detection',
    icon: Sparkles,
  },
  {
    level: 5,
    title: 'Date & Time Analysis',
    detail: 'DATE_TRUNC, INTERVAL, monthly and cohort views',
    icon: CalendarDays,
  },
  {
    level: 6,
    title: 'Advanced SQL',
    detail: 'Subqueries, CTEs, EXISTS, set operations',
    icon: Braces,
  },
  {
    level: 7,
    title: 'Window Functions',
    detail: 'RANK, LAG, running totals, moving averages',
    icon: LineChart,
  },
  {
    level: 8,
    title: 'Analytics Patterns',
    detail: 'Funnels, cohorts, retention, RFM, churn',
    icon: Target,
  },
  {
    level: 9,
    title: 'PostgreSQL Features',
    detail: 'JSONB, arrays, FILTER, DISTINCT ON, generate_series',
    icon: Database,
  },
];

const FEATURES = [
  {
    icon: Terminal,
    title: 'Interactive SQL playground',
    body: 'A real editor with autocomplete, wired to a real PostgreSQL server. Write a query, run it, and see the rows — not a screenshot of rows.',
  },
  {
    icon: Table2,
    title: 'Real-world datasets',
    body: 'Five schemas — e-commerce, SaaS, marketing, HR, food delivery — with tens of thousands of rows across two years, so aggregation and window functions actually mean something.',
  },
  {
    icon: LayoutGrid,
    title: 'Result-based grading',
    body: 'Your query is judged on the rows it returns, not on matching some reference string. Solve it your own way and it still counts.',
  },
  {
    icon: Trophy,
    title: 'Interview preparation',
    body: 'Timed practice on the questions analysts actually get asked: second-highest salary, top N per group, month-over-month growth, rolling averages.',
  },
];

const SAMPLE_QUERY = `SELECT
    c.country,
    DATE_TRUNC('month', o.order_date) AS month,
    SUM(o.total_amount)              AS revenue,
    SUM(SUM(o.total_amount)) OVER (
        PARTITION BY c.country
        ORDER BY DATE_TRUNC('month', o.order_date)
    ) AS running_revenue
FROM customers c
JOIN orders o ON o.customer_id = c.id
WHERE o.status = 'completed'
GROUP BY c.country, month
ORDER BY c.country, month;`;

function SectionHeading({
  eyebrow,
  title,
  body,
}: {
  eyebrow: string;
  title: string;
  body?: string;
}) {
  return (
    <div className="mx-auto max-w-2xl text-center">
      <p className="text-sm font-semibold tracking-wide text-brand-600 uppercase dark:text-brand-400">
        {eyebrow}
      </p>
      <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl dark:text-slate-50">
        {title}
      </h2>
      {body && <p className="mt-4 text-base text-slate-600 dark:text-slate-400">{body}</p>}
    </div>
  );
}

export function Landing() {
  const { user, registrationOpen } = useAuth();
  // With sign-ups paused the call to action would otherwise walk a visitor into
  // a page that tells them they cannot do the thing it just invited them to do.
  const closed = !user && registrationOpen === false;
  const primaryHref = user ? '/dashboard' : closed ? '/login' : '/register';
  const primaryLabel = user
    ? 'Continue learning'
    : closed
      ? 'Sign in'
      : 'Start Learning SQL';

  return (
    <div className="bg-slate-50 dark:bg-slate-950">
      {/* --- Hero ---------------------------------------------------------- */}
      <section className="mx-auto max-w-6xl px-6 pt-20 pb-16 sm:pt-28">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-brand-50 px-3 py-1 text-xs font-medium text-brand-700 ring-1 ring-brand-200 dark:bg-brand-950/50 dark:text-brand-300 dark:ring-brand-900">
              <Database className="size-3.5" aria-hidden="true" />
              Built for Data Analyst interviews and the job itself
            </span>

            <h1 className="mt-6 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl dark:text-slate-50">
              Master PostgreSQL for Data Analytics
            </h1>

            <p className="mt-6 text-lg text-slate-600 dark:text-slate-400">
              Learn SQL by solving real-world analytics problems.
            </p>

            <p className="mt-4 text-base text-slate-600 dark:text-slate-400">
              Nine levels, from your first{' '}
              <code className="rounded bg-slate-200 px-1 py-0.5 font-mono text-sm dark:bg-slate-800">
                SELECT
              </code>{' '}
              to cohort retention and window functions — every concept followed immediately by a
              query you write yourself.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={primaryHref}>
                <Button size="lg" className="px-8">
                  {primaryLabel}
                </Button>
              </Link>
              <Link to="/playground">
                <Button variant="secondary" size="lg">
                  Try the playground
                </Button>
              </Link>
            </div>

            <dl className="mt-10 grid max-w-md grid-cols-3 gap-4 text-center">
              {[
                ['50', 'lessons'],
                ['95', 'exercises'],
                ['5', 'datasets'],
              ].map(([value, label]) => (
                <div key={label}>
                  <dt className="text-2xl font-bold text-slate-900 dark:text-slate-100">{value}</dt>
                  <dd className="text-xs tracking-wide text-slate-500 uppercase dark:text-slate-400">
                    {label}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          {/* A real query, not a decorative blob: this is the level the course
              builds you up to by Level 7. */}
          <Card className="overflow-hidden p-0 shadow-xl">
            <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-100 px-4 py-2.5 dark:border-slate-800 dark:bg-slate-800/60">
              <span className="size-2.5 rounded-full bg-red-400" />
              <span className="size-2.5 rounded-full bg-amber-400" />
              <span className="size-2.5 rounded-full bg-green-400" />
              <span className="ml-2 font-mono text-xs text-slate-500 dark:text-slate-400">
                running_revenue.sql — ecommerce
              </span>
            </div>
            <pre className="thin-scrollbar overflow-x-auto bg-slate-950 p-5 font-mono text-[13px] leading-relaxed text-slate-200">
              <code>{SAMPLE_QUERY}</code>
            </pre>
            <div className="border-t border-slate-200 px-4 py-2.5 text-xs text-slate-500 dark:border-slate-800 dark:text-slate-400">
              24 rows · 41 ms
            </div>
          </Card>
        </div>
      </section>

      {/* --- What you'll learn --------------------------------------------- */}
      <section className="border-t border-slate-200 bg-white py-20 dark:border-slate-800 dark:bg-slate-900/40">
        <div className="mx-auto max-w-6xl px-6">
          <SectionHeading
            eyebrow="What you'll learn"
            title="The SQL that analysts actually write"
            body="No database administration detours. Every lesson exists because the skill shows up in analyst work or in analyst interviews."
          />

          <div className="mt-14 grid gap-6 sm:grid-cols-2">
            {FEATURES.map(({ icon: Icon, title, body }) => (
              <Card key={title} className="p-6">
                <Icon className="size-6 text-brand-600 dark:text-brand-400" aria-hidden="true" />
                <h3 className="mt-4 text-base font-semibold text-slate-900 dark:text-slate-100">
                  {title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                  {body}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* --- Curriculum ----------------------------------------------------- */}
      <section className="py-20">
        <div className="mx-auto max-w-6xl px-6">
          <SectionHeading
            eyebrow="Curriculum"
            title="Nine levels, in the order that builds"
            body="Each level assumes the one before it. Difficulty rises through a prerequisite chain, not a random shuffle."
          />

          <ol className="mt-14 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {CURRICULUM.map(({ level, title, detail, icon: Icon }) => (
              <li key={level}>
                <Card className="flex h-full items-start gap-4 p-5">
                  <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700 dark:bg-brand-950/60 dark:text-brand-300">
                    <Icon className="size-4.5" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="text-xs font-medium tracking-wide text-slate-400 uppercase dark:text-slate-400">
                      Level {level}
                    </p>
                    <h3 className="mt-0.5 font-semibold text-slate-900 dark:text-slate-100">
                      {title}
                    </h3>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{detail}</p>
                  </div>
                </Card>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* --- Progress tracking ---------------------------------------------- */}
      <section className="border-t border-slate-200 bg-white py-20 dark:border-slate-800 dark:bg-slate-900/40">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-6 lg:grid-cols-2">
          <div>
            <p className="text-sm font-semibold tracking-wide text-brand-600 uppercase dark:text-brand-400">
              Progress tracking
            </p>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
              Know exactly what you are weak at
            </h2>
            <p className="mt-4 text-base text-slate-600 dark:text-slate-400">
              Accuracy is tracked per topic, so the dashboard can tell you that your joins are solid
              and your window functions are not — and send you straight to the lesson that fixes it.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-slate-600 dark:text-slate-400">
              {[
                'XP and levels earned from solved exercises',
                'Daily streaks counted in your own timezone',
                'Per-topic accuracy that surfaces weak areas',
                'Full query history you can reopen and rerun',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-brand-500" />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <Card className="p-6">
            <p className="text-sm text-slate-500 dark:text-slate-400">PostgreSQL progress</p>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="text-3xl font-bold text-slate-900 dark:text-slate-100">72%</span>
              <span className="text-sm text-slate-500 dark:text-slate-400">
                Level 11 · Window Function Adept
              </span>
            </div>
            <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
              <div className="h-full w-[72%] rounded-full bg-brand-600" />
            </div>

            <div className="mt-6 grid grid-cols-3 gap-4 border-t border-slate-200 pt-5 dark:border-slate-800">
              {[
                ['128', 'solved'],
                ['84%', 'accuracy'],
                ['7', 'day streak'],
              ].map(([value, label]) => (
                <div key={label}>
                  <p className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                    {value}
                  </p>
                  <p className="text-xs tracking-wide text-slate-500 uppercase dark:text-slate-400">
                    {label}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-5 rounded-lg bg-slate-100 p-3 dark:bg-slate-800/60">
              <p className="text-xs tracking-wide text-slate-500 uppercase dark:text-slate-400">
                Weak areas
              </p>
              <p className="mt-1 text-sm text-slate-700 dark:text-slate-300">
                Window Functions · CTEs
              </p>
            </div>
          </Card>
        </div>
      </section>

      {/* --- CTA ------------------------------------------------------------ */}
      <section className="py-24">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl dark:text-slate-50">
            Ready to write your first query?
          </h2>
          <p className="mt-4 text-lg text-slate-600 dark:text-slate-400">
            Start at Level 1. You will be running a real query against a real database in under a
            minute.
          </p>
          <div className="mt-8">
            <Link to={primaryHref}>
              <Button size="lg" className="px-10">
                {primaryLabel}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
